<template>
  <div>
    <v-row justify="end">
      <v-col
        v-if="$route.name === 'OrganizationProfile'"
        cols="12"
        sm="6"
        md="4"
        lg
      >
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="employee"
          :items="employees"
          item-value="_id"
          item-text="name"
          flat
          solo
          :label="$t('reports.employee')"
          class="rounded-lg"
          clearable
        >
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ item.name }}
              {{ item.email ? `(${item.email})` : "" }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="4" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="training"
          :items="trainings"
          item-value="_id"
          flat
          solo
          :label="$t('user.registration.training')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.title) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.title) }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="4" lg>
        <TrainersDropDown v-model="trainer" :disabled="loading" />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg>
        <v-select
          :disabled="loading"
          v-model="status"
          hide-details
          :items="statusArr"
          item-value="value"
          solo
          flat
          :label="$t('reports.revenues.status')"
          class="rounded-lg"
          clearable
        >
          <template v-slot:selection="{ item }">
            {{ formatStatus(item) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ formatStatus(item) }}
            </div>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="4" lg>
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              v-model="formattedStartDate"
              :label="$t('reports.chooseFromDate')"
              class="rounded-lg"
              flat
              solo
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>
          <v-date-picker
            color="primary"
            v-model="startDate"
            no-title
            @input="startDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="4" lg>
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              class="rounded-lg"
              :label="$t('reports.chooseToDate')"
              v-model="formattedEndDate"
              flat
              solo
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>

          <v-date-picker
            color="primary"
            v-model="endDate"
            no-title
            @input="endDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TrainersDropDown from "@/app/user-module/components/TrainersDropDown";

export default {
  components: { TrainersDropDown },
  data() {
    return {
      employee: null,
      employees: [],
      training: null,
      trainer: null,
      trainings: [],
      status: null,
      statusArr: ["REGISTERED", "THIRDPARTY", "COMPLETED"],
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    selectedOrganization: null,
  },
  computed: {
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
    formattedEndDate: {
      get() {
        return this.endDate
          ? this.$moment(this.endDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.endDate = value;
      },
    },
  },

  watch: {
    startDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },
    endDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },

    training() {
      this.$emit("filter", "training", this.training);
    },
    trainer() {
      this.$emit("filter", "trainer", this.trainer);
    },
    employee() {
      this.$emit("filter", "employee", this.employee);
    },
    status() {
      this.$emit("filter", "status", this.status);
    },
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../../course-module/api/query/getTrainings.gql"),
        variables: {
          limit: 0,
          skip: 0,
        },
        result({ data, loading }) {
          if (data && data.getTrainings && data.getTrainings.trainings) {
            this.trainings = data.getTrainings.trainings;
          }
        },
      };
    },
    getEmployees() {
      return {
        query: require("../../../user-module/api/query/getEmployees.gql"),
        variables: {
          limit: 0,
          skip: 0,
        },
        result({ data, loading }) {
          if (data?.getEmployees) {
            this.employees = data.getEmployees.employees.reduce(
              (result, employee) => {
                const obj = {
                  _id: employee._id,
                  name: `${employee?.user?.firstName || ""} ${
                    employee?.user?.lastName || ""
                  }`,
                  email: employee?.user?.email,
                };

                if (this.selectedOrganization) {
                  if (
                    employee.organization?._id === this.selectedOrganization
                  ) {
                    result.push(obj);
                  }
                } else {
                  result.push(obj);
                }

                return result;
              },
              []
            );
          }
        },
      };
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    formatDateServer(date) {
      if (!date) return null;

      const [day, month, year] = date.split("/");
      return `${year}-${month}-${day}`;
    },
    formatStatus(status) {
      if (!status) return "-";
      if (status === "THIRDPARTY") return "ThirdParty";
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
  },
};
</script>
